import { extractValueFromCell } from "./extract-value-from-cell";

/**
 * getAccountAndLevel
 *
 * 특정 테이블 행(<tr>)에서 계정명(select 선택값)과 계정의 레벨(계층 깊이)을 추출하여 반환합니다.
 *
 * 📌 사용 목적:
 * - 원시 HTML <tr>에서 계정명과 계정 레벨(depth)을 파싱해 채점/저장용 데이터로 변환하기 위해 사용
 *
 * @param tr - 계정 정보가 포함된 <tr> 엘리먼트
 * @returns [계정명, 계정 레벨] 형태의 튜플 반환
 *          - 계정명: 사용자가 select에서 선택한 문자열 값
 *          - 계정 레벨: 계층 깊이를 나타내는 정수 (최상위는 1부터 시작)
 *
 * @throws 에러 - 계정명이 없거나 select가 없을 경우 "계정명이 필요합니다" 에러 발생
 */
export function getAccountAndLevel(tr: HTMLTableRowElement): [string, number] {
  const tds = tr.getElementsByTagName("td"); // 현재 행의 모든 <td> 요소를 가져옴

  /**
   * 계정명이 select 박스로 입력되었는지 확인
   * - tds[0]에 select가 포함되어 있으면 계정명이 select를 통해 선택된 것
   * - select가 없으면 잘못된 구조로 판단하여 에러 반환
   */
  if (tds[0]) {
    const select = tds[0].getElementsByTagName("select")[0];
    if (!select) {
      return [extractValueFromCell(tds[0]), 0];
    }
    // throw new Error("계정명이 필요합니다"); // select가 없으면 에러

    // const selected = select.getAttribute("value") ?? ""; // 사용자가 선택한 계정명
    const selected = select.value || select.getAttribute("value") || "";

    /**
     * select의 className에서 depth 정보를 추출
     * - className 중 'depth-숫자' 형태를 찾아 계층 깊이(depth)로 사용
     * - 예: class="depth-2"이면 depth = 2
     */
    const regex = /depth-(\d+)/;
    const match = regex.exec(select.className);

    // depth 값을 정수로 파싱, 없으면 0으로 처리
    const depth = match ? parseInt(match[1] ?? "0", 10) : 0;

    /**
     * 레벨 계산:
     * - depth: 0 기반 계층 값
     * - 반환 시 depth + 1 하여 1부터 시작하도록 처리
     *
     * 예:
     * - class="depth-0" → depth=0 → 반환: 1
     * - class="depth-2" → depth=2 → 반환: 3
     */
    return [selected, depth + 1];
  } else {
    /**
     * tds[0]이 없거나 select 요소가 없을 경우
     * 즉, 계정명이 작성되지 않은 잘못된 상태이므로 에러 반환
     */
    throw new Error("계정명이 필요합니다");
  }
}
